<template>
  <div v-if="visible" class="seedance-guide" role="dialog" aria-modal="true" @click="close">
    <div class="guide-card" :style="cardStyle" @click.stop>
      <button class="close-btn" type="button" aria-label="Close" @click.stop="close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <!-- 顶部：Seedance demo 视频（圆角）-->
      <div class="video-hero">
        <video :src="DEMO_VIDEO" :poster="DEMO_COVER" autoplay muted loop playsinline />
      </div>

      <!-- 下方：标题（右侧 NEW 徽章）+ 副标题 + CTA -->
      <div class="guide-body">
        <h2 class="guide-title">
          {{ t('seedance.guide.title') }}
          <span class="badge">{{ t('seedance.guide.badge') }}</span>
        </h2>
        <p class="guide-desc">{{ t('seedance.guide.description') }}</p>
        <button class="cta-btn" type="button" @click="handleGo">
          {{ t('seedance.guide.cta') }}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Seedance 2.0 引导弹窗（What's New 风格，自控）。
 *   触发：用户登录后 ~1.5s（useAuthStore().isLogin）
 *   点 CTA / 主体 → /editor?type=seedance；右上角 X → 关闭不跳转
 *   按 CURRENT_UPDATE 版本频控：以后有新模型/更新只需改版本号，即对所有用户重弹一次。
 */
const { t } = useI18n();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();

const visible = ref(false);

const CURRENT_UPDATE = '2026-07-seedance-2.0';
const SEEN_KEY = 'seedance-update-seen';
const DEMO_VIDEO = '/images/features/seedance-demo.mp4';
const DEMO_COVER = '/images/features/seedance-demo-cover.webp';
const GLOW_BG = '/images/guide-glow-dark.png';

const cardStyle = {
  backgroundImage: `url(${GLOW_BG})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

let timer: ReturnType<typeof setTimeout> | null = null;

watch(
  () => authStore.isLogin,
  (isLogin) => {
    if (!import.meta.client || !isLogin) return;
    const path = route.path || '';
    // 已在 seedance 或 auth 页时不弹
    if (path.includes('/seedance') || path.includes('/ai-studio') || path.includes('/auth')) return;
    // 已看过「当前这次更新」就不再弹（版本变了会重新弹）
    if (localStorage.getItem(SEEN_KEY) === CURRENT_UPDATE) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      visible.value = true;
    }, 1500);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer);
});

const markSeen = () => {
  try {
    if (import.meta.client) localStorage.setItem(SEEN_KEY, CURRENT_UPDATE);
  } catch {}
};

const close = () => {
  markSeen();
  visible.value = false;
};

const handleGo = () => {
  markSeen();
  visible.value = false;
  // 统一走 editor 路由（与 NewHeader / EditorCTA 一致），而非独立的 /seedance/create
  router.push({ path: localePath('/editor'), query: { type: 'seedance' } });
};
</script>

<style scoped>
.seedance-guide {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  padding: 1rem;
}
.guide-card {
  position: relative;
  width: 100%;
  max-width: 460px;
  border-radius: 1rem;
  padding: 1rem;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}
.close-btn {
  position: absolute;
  right: 0.75rem;
  top: 0.75rem;
  z-index: 2;
  display: flex;
  padding: 0.4rem;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}
.video-hero {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #000;
}
.video-hero video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.guide-body {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 1rem 0.25rem 0;
}
.guide-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.15;
  color: #fff;
}
.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: linear-gradient(90deg, #d946ef, #f43f5e);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.4px;
}
.guide-desc {
  margin: 0;
  font-size: 1rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.7);
}
.cta-btn {
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.65rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background: #fff;
  color: #000;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.cta-btn:hover {
  background: rgba(255, 255, 255, 0.9);
}
</style>
